import { useCallback } from 'react';
import {
  INSIGHT_DASHBOARD_CONFIG_NAME,
  INSIGHT_DASHBOARD_ID_ATTR,
  INSIGHT_DASHBOARD_SUB_TYPE,
  useCreateInsightDashboardAnalystMutation,
  useDeleteAiWorkersMutation,
  useLazyGetInsightDashboardAnalystsQuery,
  useLazyQueryInsightConfigsQuery,
  useUpdateInsightAgentWorkerMutation,
  useUpdateInsightConfigMutation,
  type AIWorker,
  type InsightConfigRow,
} from '@circuitry-ai/doc-data';
import { errorMessage } from '../insightAgents/hooks';
import type { DashboardConfig, DashboardVisibility } from './widgets';

/**
 * A dashboard shared with the tenant is listed in All Analysts as an analyst aiworker (sub_type
 * insight_dashboard, attribute dashboard_id). Its sharing decides the listing: shared, it is listed
 * under its title; private or deleted, it is not. DOC gives every new analyst `read` for all groups,
 * which is why a private dashboard is never listed.
 */
export interface ListedDashboard {
  readonly configId: string;
  readonly title: string;
  readonly description: string;
  readonly summary?: string;
  readonly visibility: DashboardVisibility;
}

const MAX_DESCRIPTION = 300;
const NAME_SUFFIXES = 5;

/** Its description, or else the first sentence of its summary: the card's one line. */
export function analystDescription(dashboard: Pick<ListedDashboard, 'description' | 'summary'>): string {
  const own = dashboard.description.trim();
  if (own) return own.slice(0, MAX_DESCRIPTION);
  const first = (dashboard.summary ?? '').trim().split(/(?<=[.!?])\s+/)[0] ?? '';
  return first.slice(0, MAX_DESCRIPTION);
}

/**
 * The names to try, in turn. DOC keeps aiworker and advisor names unique per tenant, so a dashboard
 * titled like an existing analyst is listed as "<title> · Dashboard".
 */
export function analystNames(title: string): string[] {
  const base = title.trim();
  const numbered = Array.from({ length: NAME_SUFFIXES - 1 }, (_, i) => `${base} · Dashboard ${i + 2}`);
  return [base, `${base} · Dashboard`, ...numbered];
}

const nameTaken = (error: unknown) => /already exists/i.test(errorMessage(error));

/** The dashboard a dashboard analyst lists ('' when it is not one). */
export function listedDashboardId(worker: Pick<AIWorker, 'sub_type' | 'aiworkers_attrs'> | null | undefined): string {
  if (String(worker?.sub_type ?? '').toLowerCase() !== INSIGHT_DASHBOARD_SUB_TYPE) return '';
  const value = worker?.aiworkers_attrs?.find((attr) => attr?.attr_name === INSIGHT_DASHBOARD_ID_ATTR)?.attr_value;
  return typeof value === 'string' ? value.trim() : '';
}

/** Tries each name until DOC accepts one; any other refusal stops at once. */
async function withFreeName<T>(title: string, attempt: (name: string) => Promise<T>): Promise<T> {
  let last: unknown;
  for (const name of analystNames(title)) {
    try {
      return await attempt(name);
    } catch (error) {
      if (!nameTaken(error)) throw error;
      last = error;
    }
  }
  throw last;
}

/** Keeps the dashboards' analysts in step with the dashboards. Every call is safe to repeat. */
export function useDashboardAnalysts() {
  const [listAnalysts] = useLazyGetInsightDashboardAnalystsQuery();
  const [createAnalyst] = useCreateInsightDashboardAnalystMutation();
  const [renameAnalyst] = useUpdateInsightAgentWorkerMutation();
  const [deleteAnalyst] = useDeleteAiWorkersMutation();

  /** Every dashboard analyst this user may read, by the dashboard each lists. */
  const listed = useCallback(async () => {
    const byDashboard = new Map<string, AIWorker[]>();
    for (const worker of await listAnalysts().unwrap()) {
      const id = listedDashboardId(worker);
      if (id) byDashboard.set(id, [...(byDashboard.get(id) ?? []), worker]);
    }
    return byDashboard;
  }, [listAnalysts]);

  const remove = useCallback(async (workers: AIWorker[]) => {
    for (const worker of workers) await deleteAnalyst(worker.aiworker_id).unwrap();
  }, [deleteAnalyst]);

  const create = useCallback((dashboard: ListedDashboard) => withFreeName(dashboard.title, (name) =>
    createAnalyst({ name, description: analystDescription(dashboard), dashboard_id: dashboard.configId }).unwrap()
  ), [createAnalyst]);

  /**
   * After a save, a share or a delete (pass visibility 'private'): a shared dashboard is listed
   * under its title and description, anything else is not listed. A second analyst for the same
   * dashboard (two saves at once) is removed.
   */
  const sync = useCallback(async (dashboard: ListedDashboard | { readonly configId: string; readonly visibility: 'private' }) => {
    const found = (await listed()).get(dashboard.configId) ?? [];
    if (dashboard.visibility !== 'tenant' || !('title' in dashboard)) {
      await remove(found);
      return;
    }
    const [kept, ...extra] = found;
    await remove(extra);
    if (!kept) {
      await create(dashboard);
      return;
    }
    const description = analystDescription(dashboard);
    const nameFits = analystNames(dashboard.title).includes(kept.name);
    if (nameFits && (kept.description ?? '') === description) return;
    await (nameFits
      ? renameAnalyst({ aiworker_id: kept.aiworker_id, name: kept.name, description }).unwrap()
      : withFreeName(dashboard.title, (name) => renameAnalyst({ aiworker_id: kept.aiworker_id, name, description }).unwrap()));
  }, [create, listed, remove, renameAnalyst]);

  /** The viewer's shared dashboards that were saved before they were listed (or whose listing failed). */
  const listMissing = useCallback(async (dashboards: readonly ListedDashboard[]) => {
    const shared = dashboards.filter((dashboard) => dashboard.visibility === 'tenant');
    if (shared.length === 0) return;
    const byDashboard = await listed();
    for (const dashboard of shared) {
      if (!byDashboard.has(dashboard.configId)) await create(dashboard);
    }
  }, [create, listed]);

  return { sync, listMissing };
}

/** A dashboard as it is listed, from its saved configuration. */
export const listedFrom = (configId: string, config: Pick<DashboardConfig, 'title' | 'description' | 'summary' | 'visibility'>): ListedDashboard => ({
  configId,
  title: config.title ?? '',
  description: config.description ?? '',
  summary: config.summary,
  visibility: config.visibility,
});

/**
 * Takes a dashboard off All Analysts (the list's Delete): it becomes private again, so its owner
 * keeps it and may share it again, and its analyst is removed.
 */
export function useUnpublishDashboard() {
  const [queryConfigs] = useLazyQueryInsightConfigsQuery();
  const [updateConfig] = useUpdateInsightConfigMutation();
  const { sync } = useDashboardAnalysts();
  return useCallback(async (configId: string) => {
    const rows = await queryConfigs({ config_name: INSIGHT_DASHBOARD_CONFIG_NAME, config_id: configId, is_active: true }).unwrap();
    const row = rows[0] as unknown as InsightConfigRow<DashboardConfig> | undefined;
    if (row?.config?.visibility === 'tenant') {
      await updateConfig({ configuration_id: row.configuration_id, name: row.config.title, config: { ...row.config, visibility: 'private' } }).unwrap();
    }
    await sync({ configId, visibility: 'private' });
  }, [queryConfigs, sync, updateConfig]);
}
